// Public Storefront API Service
// Customer-facing API calls for browsing shops and products

import { apiClient } from '@/services/api'
import type {
  PublicShopListItem,
  PublicShopFilters,
  PublicProductListItem,
  PublicProductFilters,
  PaginatedResult,
  Shop,
  Product,
  ProductCategory,
  ShopOpeningHoursDto,
  ShopTermsAndConditions,
  AttractiveExtra as _AttractiveExtra,
  PublicPromoCode
} from '@/types/shop'

// Re-export used types for consumers of this service
export type { Shop, Product, ProductCategory, ShopOpeningHoursDto, ShopTermsAndConditions }
type AttractiveExtra = _AttractiveExtra

export interface ProductFilters {
  search?: string
  categoryId?: string
  /** Roll-up filter: returns products under this base category at any depth. */
  baseCategoryId?: string
  inStock?: boolean
  minPrice?: number
  maxPrice?: number
  // Dietary filters
  isVegan?: boolean
  isVegetarian?: boolean
  isHalal?: boolean
  page?: number
  pageSize?: number
  sortBy?: string
  sortDirection?: 'asc' | 'desc'
}

// ============================================================================
// Global Autocomplete — shape from backend GET /api/search/autocomplete
// ============================================================================

export interface AutocompleteProduct {
  id: string
  name: string
  slug?: string
  price: number
  primaryImageUrl?: string | null
  shopCode: string
  shopName: string
}

export interface AutocompleteShop {
  id: string
  name: string
  code: string
  bannerUrl?: string | null
  isOpen: boolean
  isNew: boolean
}

export interface AutocompleteService {
  id: string
  name: string
  slug?: string | null
  price: number
  durationMinutes: number
  primaryImageUrl?: string | null
  providerName: string
}

/**
 * One category suggestion — can be a shop base category or a
 * service category. `type` drives routing on the client:
 *   - 'shop'    → `/shops?category={key}`
 *   - 'service' → `/services?category={key}`
 */
export interface AutocompleteCategory {
  key: string
  label: string
  iconUrl?: string | null
  type: 'shop' | 'service'
}

export interface AutocompleteResponse {
  products: AutocompleteProduct[]
  shops: AutocompleteShop[]
  services: AutocompleteService[]
  categories: AutocompleteCategory[]
  total: number
}

export const storefrontService = {
  // ============================================================================
  // Shop Browsing
  // ============================================================================

  /**
   * Get list of all active shops (public)
   * Supports geo-filtering with lat/lng/radiusKm for "shops near me" feature
   */
  async getShops(filters: PublicShopFilters = {}): Promise<PaginatedResult<PublicShopListItem>> {
    const params = new URLSearchParams()

    if (filters.search) params.append('search', filters.search)
    if (filters.category) params.append('category', filters.category)
    if (filters.excludeCategories) params.append('excludeCategories', filters.excludeCategories)
    if (filters.delivery !== undefined) params.append('delivery', String(filters.delivery))
    if (filters.collection !== undefined) params.append('collection', String(filters.collection))
    if (filters.city) params.append('city', filters.city)
    // Geo-filtering (for postcode search / "shops near me")
    if (filters.lat !== undefined) params.append('lat', String(filters.lat))
    if (filters.lng !== undefined) params.append('lng', String(filters.lng))
    if (filters.radiusKm !== undefined) params.append('radiusKm', String(filters.radiusKm))
    // Quick filters
    if (filters.freeDelivery !== undefined) params.append('freeDelivery', String(filters.freeDelivery))
    if (filters.isNew !== undefined) params.append('isNew', String(filters.isNew))
    if (filters.hasDeals !== undefined) params.append('hasDeals', String(filters.hasDeals))
    if (filters.openNow !== undefined) params.append('openNow', String(filters.openNow))
    if (filters.maxDeliveryMinutes !== undefined) params.append('maxDeliveryMinutes', String(filters.maxDeliveryMinutes))
    // Pagination
    if (filters.page) params.append('page', String(filters.page))
    if (filters.pageSize) params.append('pageSize', String(filters.pageSize))
    if (filters.sortBy) params.append('sortBy', filters.sortBy)
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection)

    const queryString = params.toString()
    const url = queryString ? `/shops?${queryString}` : '/shops'

    const result = await apiClient.get<PaginatedResult<PublicShopListItem>>(url)
    // Transform logo and banner URLs to absolute
    return {
      ...result,
      items: result.items.map(shop => ({
        ...shop,
        logoUrl: this.getImageUrl(shop.logoUrl) || undefined,
        bannerUrl: this.getImageUrl(shop.bannerUrl) || undefined
      }))
    }
  },

  /**
   * Get shop details by shop code (public)
   */
  async getShop(shopCode: string): Promise<Shop> {
    const shop = await apiClient.get<Shop>(`/shops/${shopCode}`)
    // Transform logo and banner URLs to absolute
    return {
      ...shop,
      logoUrl: this.getImageUrl(shop.logoUrl) ?? undefined,
      bannerUrl: this.getImageUrl(shop.bannerUrl) ?? undefined
    }
  },

  /**
   * Get shop opening hours (public)
   */
  async getOpeningHours(shopCode: string): Promise<ShopOpeningHoursDto | null> {
    try {
      return await apiClient.get<ShopOpeningHoursDto>(`/shops/${shopCode}/opening-hours`)
    } catch {
      return null
    }
  },

  /**
   * Get shop terms and conditions (public)
   */
  async getShopTerms(shopCode: string): Promise<ShopTermsAndConditions | null> {
    try {
      return await apiClient.get<ShopTermsAndConditions>(`/shops/${shopCode}/terms`)
    } catch {
      return null
    }
  },

  // ============================================================================
  // All Products Browsing (across all shops)
  // ============================================================================

  /**
   * Get all products across all active shops (public)
   * Supports geo-filtering with lat/lng/radiusKm for "products near me" feature
   */
  async getAllProducts(filters: PublicProductFilters = {}): Promise<PaginatedResult<PublicProductListItem>> {
    const params = new URLSearchParams()

    if (filters.search) params.append('search', filters.search)
    if (filters.category) params.append('category', filters.category)
    if (filters.excludeCategories) params.append('excludeCategories', filters.excludeCategories)
    if (filters.inStock !== undefined) params.append('inStock', String(filters.inStock))
    if (filters.hasDeals !== undefined) params.append('hasDeals', String(filters.hasDeals))
    if (filters.isFeatured !== undefined) params.append('isFeatured', String(filters.isFeatured))
    if (filters.freeDelivery !== undefined) params.append('freeDelivery', String(filters.freeDelivery))
    if (filters.isNew !== undefined) params.append('isNew', String(filters.isNew))
    if (filters.openNow !== undefined) params.append('openNow', String(filters.openNow))
    if (filters.minPrice !== undefined) params.append('minPrice', String(filters.minPrice))
    if (filters.maxPrice !== undefined) params.append('maxPrice', String(filters.maxPrice))
    // Geo-filtering (for postcode search / "products near me")
    if (filters.lat !== undefined) params.append('lat', String(filters.lat))
    if (filters.lng !== undefined) params.append('lng', String(filters.lng))
    if (filters.radiusKm !== undefined) params.append('radiusKm', String(filters.radiusKm))
    // Fulfillment capability (homepage Delivery/Pickup/All toggle)
    if (filters.delivery !== undefined) params.append('delivery', String(filters.delivery))
    if (filters.collection !== undefined) params.append('collection', String(filters.collection))
    // Pagination
    if (filters.page) params.append('page', String(filters.page))
    if (filters.pageSize) params.append('pageSize', String(filters.pageSize))
    if (filters.sortBy) params.append('sortBy', filters.sortBy)
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection)

    const queryString = params.toString()
    const url = queryString ? `/shops/products?${queryString}` : '/shops/products'

    return apiClient.get<PaginatedResult<PublicProductListItem>>(url)
  },

  /**
   * Get all featured products across all active shops (public)
   */
  async getAllFeaturedProducts(count: number = 12): Promise<PublicProductListItem[]> {
    return apiClient.get<PublicProductListItem[]>(`/shops/products/featured?count=${count}`)
  },

  /**
   * Get products on offer across all active shops (public)
   * Returns products where CompareAtPrice > Price, sorted by highest discount first
   */
  async getProductsOnOffer(count: number = 12): Promise<PublicProductListItem[]> {
    return apiClient.get<PublicProductListItem[]>(`/shops/products/offers?count=${count}`)
  },

  /**
   * Get total count of featured products across all active shops
   */
  async getFeaturedProductsCount(): Promise<number> {
    const response = await apiClient.get<{ count: number }>('/shops/products/featured/count')
    return response.count
  },

  /**
   * Get total count of products on offer across all active shops
   */
  async getProductsOnOfferCount(): Promise<number> {
    const response = await apiClient.get<{ count: number }>('/shops/products/offers/count')
    return response.count
  },

  /**
   * Get attractive extras (free or cheap) across all active shops (public)
   * Great for promotional display showing customers they can get free/discounted add-ons
   */
  async getAttractiveExtras(maxPrice: number = 0.99, count: number = 12): Promise<AttractiveExtra[]> {
    return apiClient.get<AttractiveExtra[]>(`/shops/extras/attractive?maxPrice=${maxPrice}&count=${count}`)
  },

  /**
   * Get total count of attractive extras (free or cheap) across all active shops
   */
  async getAttractiveExtrasCount(maxPrice: number = 0.99): Promise<number> {
    const response = await apiClient.get<{ count: number }>(`/shops/extras/attractive/count?maxPrice=${maxPrice}`)
    return response.count
  },

  // ============================================================================
  // Product Browsing (shop-specific)
  // ============================================================================

  /**
   * Get products for a shop (public)
   */
  async getProducts(
    shopCode: string,
    filters: ProductFilters = {}
  ): Promise<PaginatedResult<Product>> {
    const params = new URLSearchParams()

    if (filters.search) params.append('search', filters.search)
    if (filters.categoryId) params.append('categoryId', filters.categoryId)
    if (filters.baseCategoryId) params.append('baseCategoryId', filters.baseCategoryId)
    if (filters.inStock !== undefined) params.append('inStock', String(filters.inStock))
    if (filters.minPrice !== undefined) params.append('minPrice', String(filters.minPrice))
    if (filters.maxPrice !== undefined) params.append('maxPrice', String(filters.maxPrice))
    // Dietary filters
    if (filters.isVegan !== undefined) params.append('isVegan', String(filters.isVegan))
    if (filters.isVegetarian !== undefined) params.append('isVegetarian', String(filters.isVegetarian))
    if (filters.isHalal !== undefined) params.append('isHalal', String(filters.isHalal))
    if (filters.page) params.append('page', String(filters.page))
    if (filters.pageSize) params.append('pageSize', String(filters.pageSize))
    if (filters.sortBy) params.append('sortBy', filters.sortBy)
    if (filters.sortDirection) params.append('sortDirection', filters.sortDirection)

    const queryString = params.toString()
    const url = queryString
      ? `/shops/${shopCode}/products?${queryString}`
      : `/shops/${shopCode}/products`

    return apiClient.get<PaginatedResult<Product>>(url)
  },

  /**
   * Get featured products for a shop (public)
   */
  async getFeaturedProducts(shopCode: string, count: number = 10): Promise<Product[]> {
    return apiClient.get<Product[]>(`/shops/${shopCode}/products/featured?count=${count}`)
  },

  /**
   * Get product details by slug (public)
   */
  async getProductBySlug(shopCode: string, slug: string): Promise<Product> {
    return apiClient.get<Product>(`/shops/${shopCode}/products/${slug}`)
  },

  /**
   * Get related products (public)
   */
  async getRelatedProducts(shopCode: string, slug: string, count: number = 4): Promise<Product[]> {
    return apiClient.get<Product[]>(`/shops/${shopCode}/products/${slug}/related?count=${count}`)
  },

  // ============================================================================
  // Categories
  // ============================================================================

  /**
   * Get active categories for a shop (public)
   */
  async getCategories(shopCode: string): Promise<ProductCategory[]> {
    return apiClient.get<ProductCategory[]>(`/shops/${shopCode}/categories`)
  },

  /**
   * Get products by category (public)
   */
  async getProductsByCategory(
    shopCode: string,
    categoryId: string,
    page: number = 1,
    pageSize: number = 20
  ): Promise<PaginatedResult<Product>> {
    return apiClient.get<PaginatedResult<Product>>(
      `/shops/${shopCode}/categories/${categoryId}/products?page=${page}&pageSize=${pageSize}`
    )
  },

  // ============================================================================
  // Public Promo Codes / Vouchers
  // ============================================================================

  /**
   * Get all public active promo codes across all shops
   * For displaying on homepage "Hot Deals" section and /deals page
   */
  async getPublicPromoCodes(params: {
    page?: number
    pageSize?: number
    shopCategory?: string
    promoType?: string
  } = {}): Promise<{ promoCodes: PublicPromoCode[], totalCount: number }> {
    const queryParams = new URLSearchParams()
    if (params.page) queryParams.append('page', String(params.page))
    if (params.pageSize) queryParams.append('pageSize', String(params.pageSize))
    if (params.shopCategory) queryParams.append('shopCategory', params.shopCategory)
    if (params.promoType) queryParams.append('promoType', params.promoType)

    const queryString = queryParams.toString()
    const url = queryString ? `/promo-codes/public?${queryString}` : '/promo-codes/public'

    const result = await apiClient.get<{ promoCodes: PublicPromoCode[], totalCount: number }>(url)

    // Transform shop logo URLs to absolute
    return {
      ...result,
      promoCodes: result.promoCodes.map(promo => ({
        ...promo,
        shopLogoUrl: this.getImageUrl(promo.shopLogoUrl) ?? undefined
      }))
    }
  },

  /**
   * Get public promo codes for a specific shop
   */
  async getShopPublicPromoCodes(shopCode: string): Promise<PublicPromoCode[]> {
    const result = await apiClient.get<PublicPromoCode[]>(`/shops/${shopCode}/promo-codes/public`)
    return result.map(promo => ({
      ...promo,
      shopLogoUrl: this.getImageUrl(promo.shopLogoUrl) ?? undefined
    }))
  },

  // ============================================================================
  // Global Autocomplete
  // ============================================================================

  /**
   * Global storefront autocomplete for the header search dropdown.
   * Returns products, shops, services and categories that match the
   * query. Signature intentionally supports an AbortSignal so callers
   * can cancel in-flight requests when the user keeps typing.
   *
   * The backend already applies:
   *   - min-length 2 guard (returns an empty payload otherwise)
   *   - retail-only (wholesale shops/products excluded)
   *   - UK-postcode / city detection for the services city filter
   *
   * Image URLs on products / shops / services can be relative paths
   * (self-hosted /images/... in dev). Callers should run them through
   * `storefrontService.getImageUrl` before rendering.
   */
  async getAutocomplete(
    q: string,
    opts: {
      limit?: number
      latitude?: number
      longitude?: number
      signal?: AbortSignal
    } = {}
  ): Promise<AutocompleteResponse> {
    // Local guard — mirrors the backend's <2 char guard so we don't
    // even fire the request for one keystroke.
    const trimmed = q?.trim() ?? ''
    if (trimmed.length < 2) {
      return { products: [], shops: [], services: [], categories: [], total: 0 }
    }

    const params = new URLSearchParams()
    params.append('q', trimmed)
    if (opts.limit) params.append('limit', String(opts.limit))
    if (opts.latitude !== undefined) params.append('latitude', String(opts.latitude))
    if (opts.longitude !== undefined) params.append('longitude', String(opts.longitude))

    // apiClient is our shared axios instance — passing the signal via
    // the second arg lets the caller abort the request when a fresher
    // keystroke supersedes it.
    return apiClient.get<AutocompleteResponse>(
      `/search/autocomplete?${params.toString()}`,
      { signal: opts.signal }
    )
  },

  // ============================================================================
  // Helpers
  // ============================================================================

  /**
   * Transform image URL to absolute URL
   * In development, backend serves images at /images/... but frontend is on different port
   * This prepends the API base URL for relative image paths
   */
  getImageUrl(url?: string | null): string | null {
    if (!url) return null

    // Already absolute URL (S3 or other CDN)
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }

    // Relative URL - prepend backend base URL
    const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:50000/api'
    // Remove /api suffix to get the base server URL
    const serverBaseUrl = apiBaseUrl.replace(/\/api$/, '')

    return `${serverBaseUrl}${url}`
  },

  /**
   * Format delivery time for display
   */
  formatDeliveryTime(minutes?: number): string {
    if (!minutes) return ''
    if (minutes < 60) return `${minutes} min`
    const hours = Math.floor(minutes / 60)
    const remainingMins = minutes % 60
    if (remainingMins === 0) return `${hours} hr`
    return `${hours} hr ${remainingMins} min`
  },

  /**
   * Format delivery fee for display
   */
  formatDeliveryFee(fee?: number, freeThreshold?: number): string {
    if (fee === undefined || fee === null || fee === 0) return 'Free Delivery'
    if (freeThreshold && freeThreshold > 0) {
      return `Delivery from GBP ${fee.toFixed(2)} (Free over GBP ${freeThreshold.toFixed(2)})`
    }
    return `Delivery GBP ${fee.toFixed(2)}`
  },

  /**
   * Format currency
   */
  formatCurrency(amount: number): string {
    return `GBP ${amount.toFixed(2)}`
  },

  /**
   * Get shop URL
   */
  getShopUrl(shopCode: string): string {
    return `/shop/${shopCode}`
  },

  /**
   * Get product URL
   */
  getProductUrl(shopCode: string, slug: string): string {
    return `/shop/${shopCode}/product/${slug}`
  }
}

export default storefrontService
